import {
  BOOKING_STEP_KINDS,
  bookingStepSchema,
  type BookingQuestion,
  type BookingStep,
  type BookingStepKind,
  type BookingsSection,
} from '@/features/storefront/sections/schema';

/**
 * The steps of a step-by-step bookings form, in the order the shopper meets
 * them - the merchant's list, made safe to walk.
 *
 * The merchant chooses, orders and renames the steps in the customiser, and
 * the document it saves can say anything: a step twice, a step missing, the
 * review in the middle, the date asked before the service whose length decides
 * which times exist. Rather than trust the editor to have prevented all of
 * that, every reader runs the same rules - this function on the shop, and
 * `BookingsSection`'s validator on the server (`backend/app/schemas/store.py`),
 * which the two must agree with line for line:
 *
 * 1. Each kind at most once; a later duplicate is dropped.
 * 2. A missing kind is appended, in the default order, visible and unnamed -
 *    a document written before steps existed walks all five.
 * 3. `details` is always visible: it is where the name is asked, and a booking
 *    nobody can be called back about is not one. Likewise `datetime`, which is
 *    the appointment.
 * 4. `service` and `person` come before `datetime`: the service's length and
 *    the person's diary are what decide which times there are. One found after
 *    it moves to just before it, the two keeping their relative order.
 * 5. A visible `review` is last - a summary with a question after it is not a
 *    summary. A hidden one stays where it was, since nothing reads its place.
 *
 * The list returned is the whole list, hidden steps included, because the
 * customiser lists those too. What the shopper walks is `activeBookingSteps`.
 */
export function bookingSteps(section: Pick<BookingsSection, 'steps'>): BookingStep[] {
  // Rule 1: first of each kind wins.
  const seen = new Set<BookingStepKind>();
  const steps: BookingStep[] = [];
  for (const step of section.steps ?? []) {
    if (!BOOKING_STEP_KINDS.includes(step.kind) || seen.has(step.kind)) continue;
    seen.add(step.kind);
    steps.push({ ...step });
  }

  // Rule 2: what is missing joins at the end, in the default order.
  for (const kind of BOOKING_STEP_KINDS) {
    if (!seen.has(kind)) steps.push(bookingStepSchema.parse({ kind }));
  }

  // Rule 3: the two steps a booking cannot be made without.
  for (const step of steps) {
    if (step.kind === 'details' || step.kind === 'datetime') step.visible = true;
  }

  // Rule 4: service and person before the date, keeping their own order.
  const at = steps.findIndex((step) => step.kind === 'datetime');
  const late = steps.filter(
    (step, index) => index > at && (step.kind === 'service' || step.kind === 'person'),
  );
  if (late.length > 0) {
    const rest = steps.filter((step) => !late.includes(step));
    const datetime = rest.findIndex((step) => step.kind === 'datetime');
    rest.splice(datetime, 0, ...late);
    steps.splice(0, steps.length, ...rest);
  }

  // Rule 5: a visible review closes the form.
  const review = steps.findIndex((step) => step.kind === 'review');
  if (review >= 0 && steps[review].visible && review !== steps.length - 1) {
    steps.push(...steps.splice(review, 1));
  }

  return steps;
}

/** What the shop knows at the moment it decides which steps have anything to ask. */
export interface BookingStepContext {
  /** How many services the shopper could choose between. */
  serviceCount: number;
  /**
   * The person is already settled by the page - a profile's own calendar, or
   * a booking card started on someone - so asking again would be a question
   * with one answer.
   */
  personSettled?: boolean;
}

/**
 * The steps a shopper actually walks: the visible ones, less those with
 * nothing to ask *on this shop, right now*.
 *
 * `service` is skipped when there is exactly one service (no choice is no
 * question); `person` unless the section lets the shopper choose
 * (`staff_mode: "choose"`), or when the page has already chosen for them.
 * A skipped step still counts as answered - the one service is chosen, the
 * fixed person is booked - it is only not shown.
 */
export function activeBookingSteps(
  section: Pick<BookingsSection, 'steps' | 'staff_mode'>,
  context: BookingStepContext,
): BookingStep[] {
  return bookingSteps(section).filter((step) => {
    if (!step.visible) return false;
    if (step.kind === 'service') return context.serviceCount !== 1;
    if (step.kind === 'person') return section.staff_mode === 'choose' && !context.personSettled;
    return true;
  });
}

/** A shopper's answers to the merchant's questions, by question id. */
export type BookingAnswers = Record<string, string | boolean>;

/**
 * The questions a shopper is asked: the merchant's visible ones with a label.
 *
 * A choice with no choices yet is left out too - not drawn, and so never
 * required. The customiser saves as the merchant types, so a select question
 * exists for a moment before its options do; the server keeps it and ignores
 * it when booking, and a required dropdown with nothing to pick would be a
 * form no shopper could send.
 */
export function shownQuestions(section: Pick<BookingsSection, 'questions'>): BookingQuestion[] {
  return (section.questions ?? []).filter(
    (question) =>
      question.visible &&
      question.label.trim() !== '' &&
      (question.kind !== 'select' || question.options.some((option) => option.trim() !== '')),
  );
}

/**
 * Whether one question has an answer that would satisfy the server: text
 * that is not only spaces, an option that is one of the question's own, a
 * ticked box. Every question counts as answered when it is optional - except
 * a select holding something that is not one of its options, which the
 * server refuses either way.
 */
export function questionAnswered(question: BookingQuestion, value: string | boolean | undefined): boolean {
  if (question.kind === 'checkbox') return !question.required || value === true;
  const text = typeof value === 'string' ? value.trim() : '';
  if (question.kind === 'select' && text && !question.options.includes(text)) return false;
  return !question.required || text !== '';
}

/** The first question that stops the form being sent, or null. */
export function firstUnanswered(
  questions: readonly BookingQuestion[],
  answers: BookingAnswers,
): BookingQuestion | null {
  return questions.find((question) => !questionAnswered(question, answers[question.id])) ?? null;
}

/**
 * What is posted as `answers`: one entry per shown question that has
 * something to say. Blank text is left out rather than sent as "", and a
 * checkbox is always sent - "No" is an answer the merchant asked for.
 * Empty when there are no questions, so the request is the one it always was.
 */
export function answersPayload(
  questions: readonly BookingQuestion[],
  answers: BookingAnswers,
): BookingAnswers {
  const payload: BookingAnswers = {};
  for (const question of questions) {
    const value = answers[question.id];
    if (question.kind === 'checkbox') {
      payload[question.id] = value === true;
    } else if (typeof value === 'string' && value.trim()) {
      payload[question.id] = value.trim();
    }
  }
  return payload;
}
